import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SignInIdentifier } from '@logto/schemas';

import Button from '@/components/Button';
import { InputField } from '@/components/InputFields';
import VerificationCodeInput from '@/components/VerificationCode';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import { sendVerificationCodeApi } from '@/apis/utils';
import { UserFlow } from '@/types';
import { completeVerificationStep } from '@/apis/experience/verification-setup';

import type { VerificationStepProps } from '../types';

import styles from './EmailVerificationStep.module.scss';

const EmailVerificationStep = ({ step, onStepComplete, progress, userEmail }: VerificationStepProps) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  
  const [verificationCode, setVerificationCode] = useState<string[]>([]);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>();

  // Use the same API as MfaChoose for sending verification code
  const asyncSendVerificationCode = useApi(sendVerificationCodeApi);
  const asyncCompleteStep = useApi(completeVerificationStep);

  // Get userId from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userIdParam = params.get('user_id');
    setUserId(userIdParam ?? undefined);
  }, []);

  // Send verification code to email using the same method as MfaChoose
  const handleSendCode = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage(undefined); // Clear any previous errors
      
      // Use the same method as MfaChoose - pass empty value and userId
      const [error, result] = await asyncSendVerificationCode(
        UserFlow.SignIn,
        {
          type: SignInIdentifier.Email,
          value: '',
        },
        userId
      );

      if (error) {
        await handleError(error, {
          'guard.invalid_input': () => {
            setErrorMessage('invalid_email');
          },
        });
        return;
      }

      if (result) {
        setVerificationId(result.verificationId);
        setIsCodeSent(true);
      }
    } catch (error) {
      await handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [userId, asyncSendVerificationCode, handleError]);

  // Verify the code and complete the step
  const handleVerifyCode = useCallback(async () => {
    const codeString = verificationCode.join('');
    if (!codeString || codeString.length !== 6 || !verificationId) return;

    try {
      setIsSubmitting(true);
      
      const [error, result] = await asyncCompleteStep('email', {
        verificationId,
        code: codeString,
      });

      if (error) {
        await handleError(error);
        return;
      }

      if (result?.success) {
        await onStepComplete('email');
      }
    } catch (error) {
      await handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [verificationCode, verificationId, asyncCompleteStep, handleError, onStepComplete]);

  if (step.status === 'completed') {
    return (
      <div className={styles.completedStep}>
        <div className={styles.completedIcon}>✅</div>
        <div className={styles.completedContent}>
          <h3>{t('mfa.email_verification_completed')}</h3>
          <p>{t('mfa.email_verification_completed_description')}</p>
          {progress?.emailVerifiedAt && (
            <div className={styles.completedTime}>
              {t('mfa.completed_at', { 
                time: new Date(progress.emailVerifiedAt).toLocaleString() 
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>{step.icon}</div>
        <div>
          <h2 className={styles.stepTitle}>{step.title}</h2>
          <p className={styles.stepDescription}>{step.description}</p>
        </div>
      </div>

      <div className={styles.stepContent}>
        {!isCodeSent ? (
          // Step 1: Send verification code
          <>
            <p className={styles.instruction}>
              {t('mfa.email_send_code_instruction')}
            </p>
            
            {userEmail && (
              <div className={styles.emailDisplay}>
                <strong>Email:</strong> {userEmail}
              </div>
            )}
            
            {errorMessage && (
              <div className={styles.errorMessage}>
                {t(errorMessage)}
              </div>
            )}
            
            <Button
              title="mfa.send_verification_code"
              type="primary"
              size="large"
              isLoading={isSubmitting}
              onClick={handleSendCode}
              className={styles.sendCodeButton}
            />
          </>
        ) : (
          // Step 2: Enter verification code
          <div className={styles.verifyCodeSection}>
            <p className={styles.instruction}>
              {t('mfa.email_enter_code_instruction')}
            </p>

            <div className={styles.codeInputSection}>
              <VerificationCodeInput
                name="verificationCode"
                value={verificationCode}
                onChange={(code) => setVerificationCode(code)}
                className={styles.codeInput}
              />
            </div>

            <div className={styles.actionButtons}>
              <Button
                title="mfa.verify_code"
                type="primary"
                size="large"
                isLoading={isSubmitting}
                isDisabled={verificationCode.join('').length !== 6}
                onClick={handleVerifyCode}
              />
              
              <Button
                title="mfa.resend_code"
                type="secondary"
                size="medium"
                onClick={() => {
                  setIsCodeSent(false);
                  setVerificationCode([]);
                  setVerificationId(null);
                }}
              />
            </div>
          </div>
        )}
      </div>

        <div className={styles.helpText}>
          {t('mfa.email_verification_help')}
        </div>
    </div>
  );
};

export default EmailVerificationStep;